import React from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton } from 'web-library';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { PaginatedData } from 'web-library/models/List';
import { AnnouncementDetail } from 'web-library/models/Announcements';
import AnnouncementSingleContent from './AnnouncementSingleContent';
import { useGetPriorityAnnouncements } from '../../../api-refactored/announcements/useGetPriorityAnnouncements';
import { useGetAnnouncementNoticeDetails } from '../../../api-refactored/announcements/useGetAnnouncementNoticeDetails';

const useStyles = makeStyles()((theme) =>
({
  announcementTitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2.5),
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(-2),
  },
}));

const displayDialogTitle = (
  t: TFunction<'translation'>,
  announcementType: string,
  priorityAnnouncements?: PaginatedData<AnnouncementDetail>,
  announcementUuid?: string
) => {
  if (announcementUuid || announcementType === 'BROADCAST_MESSAGE') {
    return <>{t('admin->>AnnouncementNoticeBoard.Notice')}</>;
  }
  return (
    <>
      {t('admin->>AnnouncementNoticeBoard.PriorityNotice', {
        number: priorityAnnouncements?.content.length,
      })}
    </>
  );
};

interface AnnouncementContentProps {
  openAnnouncementModal: boolean;
  setOpenAnnouncementModal: React.Dispatch<React.SetStateAction<boolean>>;
  isPriorityNotice: boolean;
  announcementType: string;
  announcementUuid?: string;
}

const AnnouncementContent = ({
  openAnnouncementModal,
  setOpenAnnouncementModal,
  isPriorityNotice,
  announcementType,
  announcementUuid = '',
}: AnnouncementContentProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const theme = useTheme();

  const { data: priorityAnnouncements } = useGetPriorityAnnouncements({
    requestData: {
      apiPathParam: {},
      announcementType,
    },
    enabled: isPriorityNotice,
    onSuccess: (res) => setOpenAnnouncementModal(res?.content?.length > 0 && isPriorityNotice),
  });

  const { data: announcementNoticeDetails } = useGetAnnouncementNoticeDetails({
    announcementType,
    announcementUuid: announcementUuid || '',
    enabled: !!announcementUuid && !isPriorityNotice,
  });

  const handleCloseAnnouncementModal = () => setOpenAnnouncementModal(false);

  const displayPriorityAnnouncements = (
    priorityAnnouncementPage: PaginatedData<AnnouncementDetail> | undefined
  ) =>
    priorityAnnouncementPage &&
    priorityAnnouncementPage.content.map(
      (priorityAnnouncement: AnnouncementDetail, index: number) => (
        <>
          <AnnouncementSingleContent announcementNoticeDetails={priorityAnnouncement} />
          {index !== priorityAnnouncementPage.content.length - 1 && (
            <Divider className={classes.divider} />
          )}
        </>
      )
    );

  const displayAnnouncementDetails = (announcementDetails: AnnouncementDetail | undefined) =>
    announcementDetails && (
      <AnnouncementSingleContent announcementNoticeDetails={announcementDetails} />
    );

  return (
    (<Dialog open={openAnnouncementModal} maxWidth="lg">
      <DialogTitle>
        <Box className={classes.announcementTitleContainer}>
          {displayDialogTitle(t, announcementType, priorityAnnouncements, announcementUuid)}
          <Box>
            <IconButton
              id="button-announcement-content-close"
              name={IconName.cross}
              iconSize="S"
              color={theme.color.icon.iconDefault}
              ariaLabel="announcement-content-close"
              onClick={handleCloseAnnouncementModal}
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {announcementUuid
          ? displayAnnouncementDetails(announcementNoticeDetails)
          : displayPriorityAnnouncements(priorityAnnouncements)}
      </DialogContent>
    </Dialog>)
  );
};

export default AnnouncementContent;
